import React, { useState, useEffect } from 'react';
import Navigation from '@/components/shared/Navigation';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { 
  User, 
  Mail, 
  Phone, 
  MapPin, 
  Calendar, 
  Briefcase, 
  Camera,
  Save,
  Edit
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { memberApi } from '@/lib/api';
import LoadingSpinner from '@/components/shared/LoadingSpinner';
import { toast } from '@/components/ui/use-toast';

const Profile = () => {
  const { user, setUser } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [memberData, setMemberData] = useState<any>(null);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    residential_address: '',
    occupation: '',
    emergency_contact_name: '',
    emergency_contact_phone: '',
    jersey_number: '',
    playing_level: '',
    health_conditions: '',
  });

  useEffect(() => {
    // Initialize form data from user object
    if (user && user.id) {
      // Process user data locally if needed (only if first_name and last_name are actually missing)
      let processedUser = user;
      let needsUpdate = false;
      
      if (user.name && (!user.first_name || !user.last_name)) {
        const nameParts = user.name.trim().split(' ');
        processedUser = {
          ...user,
          first_name: nameParts[0] || '',
          last_name: nameParts.slice(1).join(' ') || ''
        };
        needsUpdate = true;
      }
      
      // Only update if we actually processed the name
      if (needsUpdate) {
        setUser(processedUser);
        localStorage.setItem('user', JSON.stringify(processedUser));
      }
      
      setFormData({
        first_name: processedUser.first_name || '',
        last_name: processedUser.last_name || '',
        email: processedUser.email || '',
        phone: processedUser.phone || '',
        residential_address: processedUser.residential_address || '',
        occupation: processedUser.occupation || '',
        emergency_contact_name: processedUser.emergency_contact_name || '',
        emergency_contact_phone: processedUser.emergency_contact_phone || '',
        jersey_number: processedUser.jersey_number?.toString() || '',
        playing_level: processedUser.playing_level || '',
        health_conditions: processedUser.health_conditions || '',
      });
    }
  }, [user?.id, user?.name, user?.first_name, user?.last_name]); // Only run when these specific fields change

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      const updatedData = await memberApi.updateMyProfile(formData);
      
      // Update the user context with new data
      setUser({ ...user, ...updatedData });
      
      setIsEditing(false);
      toast({
        title: "Success",
        description: "Profile updated successfully",
      });
    } catch (error: any) {
      console.error('Error updating profile:', error);
      toast({
        title: "Error",
        description: "Failed to update profile",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    // Reset form data to original values
    if (user) {
      setFormData({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        email: user.email || '',
        phone: user.phone || '',
        residential_address: user.residential_address || '',
        occupation: user.occupation || '',
        emergency_contact_name: user.emergency_contact_name || '',
        emergency_contact_phone: user.emergency_contact_phone || '',
        jersey_number: user.jersey_number?.toString() || '',
        playing_level: user.playing_level || '',
        health_conditions: user.health_conditions || '',
      });
    }
    setIsEditing(false);
  };

  const handlePhotoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setIsLoading(true);
      await memberApi.uploadMyPhoto(file);
      toast({
        title: "Success",
        description: "Photo uploaded successfully",
      });
      
      // Refresh user data by re-fetching profile
      const updatedUser = await memberApi.getDashboard(); // Assuming getDashboard returns updated user profile, or there's a dedicated getMyProfile
      setUser(prev => ({ ...prev, ...updatedUser }));

    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to upload photo",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return dateString;
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navigation />
        <div className="flex items-center justify-center min-h-[50vh]">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold text-gray-900">My Profile</h1>
            <div className="flex space-x-3">
              {isEditing ? (
                <>
                  <Button variant="outline" onClick={handleCancel}>
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleSave} 
                    disabled={isSaving}
                    className="bg-basketball-gradient hover:opacity-90"
                  >
                    {isSaving ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="h-4 w-4 mr-2" />
                        Save Changes
                      </>
                    )}
                  </Button>
                </>
              ) : (
                <Button 
                  onClick={() => setIsEditing(true)}
                  className="bg-basketball-gradient hover:opacity-90"
                >
                  <Edit className="h-4 w-4 mr-2" />
                  Edit Profile
                </Button>
              )}
            </div>
          </div>
          <p className="text-gray-600 mt-2">
            Update your personal information and membership details
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Profile Photo Card */}
          <div className="lg:col-span-1">
            <Card>
              <CardHeader>
                <CardTitle>Profile Photo</CardTitle>
              </CardHeader>
              <CardContent className="text-center">
                <div className="relative mx-auto w-32 h-32 mb-4">
                  {user.photo ? (
                    <img 
                      src={user.photo} 
                      alt="Profile" 
                      className="w-32 h-32 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-32 h-32 bg-gray-200 rounded-full flex items-center justify-center">
                      <User className="h-16 w-16 text-gray-400" />
                  </div>
                  )}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handlePhotoUpload}
                      className="hidden"
                    id="photo-upload"
                    />
                  <label
                    htmlFor="photo-upload"
                    className="absolute bottom-0 right-0 bg-basketball-orange-500 rounded-full p-2 cursor-pointer hover:bg-basketball-orange-600 transition-colors"
                  >
                    <Camera className="h-4 w-4 text-white" />
                  </label>
                </div>
                <p className="text-sm text-gray-600">
                  Click the camera icon to update your photo
                </p>
              </CardContent>
            </Card>

            {/* Membership Info Card */}
            <Card className="mt-6">
              <CardHeader>
                <CardTitle>Membership Info</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Status</span>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    user.status === 'active' ? 'bg-green-100 text-green-800' :
                    user.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {user.status || 'Active'}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Type</span>
                  <span className="text-sm font-medium">
                    {user.membership_type || 'Active OG'}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Member Since</span>
                  <span className="text-sm font-medium">
                    {user.created_at ? formatDate(user.created_at) : 'N/A'}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Jersey Number</span>
                  <span className="text-sm font-medium">
                    {user.jersey_number ? `#${user.jersey_number}` : 'Not assigned'}
                  </span>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Profile Details */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle>Personal Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Basic Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="first_name">First Name</Label>
                    {isEditing ? (
                      <Input
                        id="first_name"
                        value={formData.first_name}
                        onChange={(e) => handleInputChange('first_name', e.target.value)}
                        className="mt-1"
                      />
                    ) : (
                      <p className="mt-1 text-sm text-gray-900">{user.first_name || 'Not provided'}</p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="last_name">Last Name</Label>
                    {isEditing ? (
                    <Input
                      id="last_name"
                        value={formData.last_name}
                      onChange={(e) => handleInputChange('last_name', e.target.value)}
                        className="mt-1"
                    />
                    ) : (
                      <p className="mt-1 text-sm text-gray-900">{user.last_name || 'Not provided'}</p>
                    )}
                </div>

                  <div>
                    <Label htmlFor="email">Email Address</Label>
                    {isEditing ? (
                    <Input
                      id="email"
                      type="email"
                        value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                        className="mt-1"
                    />
                    ) : (
                      <p className="mt-1 text-sm text-gray-900">{user.email || 'Not provided'}</p>
                    )}
                </div>

                  <div>
                    <Label htmlFor="phone">Phone Number</Label>
                    {isEditing ? (
                      <Input
                        id="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => handleInputChange('phone', e.target.value)}
                        className="mt-1"
                      />
                    ) : (
                      <p className="mt-1 text-sm text-gray-900">{user.phone || 'Not provided'}</p>
                    )}
                  </div>
                </div>

                {/* Address and Occupation */}
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="residential_address">Residential Address</Label>
                    {isEditing ? (
                      <Textarea
                        id="residential_address"
                        value={formData.residential_address}
                        onChange={(e) => handleInputChange('residential_address', e.target.value)}
                        className="mt-1"
                        rows={2}
                      />
                    ) : (
                      <p className="mt-1 text-sm text-gray-900">{user.residential_address || 'Not provided'}</p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="occupation">Occupation</Label>
                    {isEditing ? (
                    <Input
                        id="occupation"
                        value={formData.occupation}
                        onChange={(e) => handleInputChange('occupation', e.target.value)}
                        className="mt-1"
                      />
                    ) : (
                      <p className="mt-1 text-sm text-gray-900">{user.occupation || 'Not provided'}</p>
                    )}
                  </div>
                </div>

                {/* Emergency Contact */}
                <div className="border-t pt-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Emergency Contact</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="emergency_contact_name">Contact Name</Label>
                      {isEditing ? (
                        <Input
                          id="emergency_contact_name"
                          value={formData.emergency_contact_name}
                          onChange={(e) => handleInputChange('emergency_contact_name', e.target.value)}
                          className="mt-1"
                        />
                      ) : (
                        <p className="mt-1 text-sm text-gray-900">{user.emergency_contact_name || 'Not provided'}</p>
                      )}
                    </div>

                    <div>
                      <Label htmlFor="emergency_contact_phone">Contact Phone</Label>
                      {isEditing ? (
                        <Input
                          id="emergency_contact_phone"
                          type="tel"
                          value={formData.emergency_contact_phone}
                          onChange={(e) => handleInputChange('emergency_contact_phone', e.target.value)}
                          className="mt-1"
                        />
                      ) : (
                        <p className="mt-1 text-sm text-gray-900">{user.emergency_contact_phone || 'Not provided'}</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Basketball Info */}
                <div className="border-t pt-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Basketball Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                    <Label htmlFor="jersey_number">Jersey Number</Label>
                      {isEditing ? (
                    <Input
                      id="jersey_number"
                      type="number"
                          value={formData.jersey_number}
                      onChange={(e) => handleInputChange('jersey_number', e.target.value)}
                          className="mt-1"
                        />
                      ) : (
                        <p className="mt-1 text-sm text-gray-900">{user.jersey_number || 'Not assigned'}</p>
                      )}
                    </div>

                    <div>
                      <Label htmlFor="playing_level">Playing Level</Label>
                      {isEditing ? (
                        <Input
                          id="playing_level"
                          value={formData.playing_level}
                          onChange={(e) => handleInputChange('playing_level', e.target.value)}
                          className="mt-1"
                        />
                      ) : (
                        <p className="mt-1 text-sm text-gray-900">{user.playing_level || 'Not specified'}</p>
                      )}
                    </div>
                </div>

                  <div className="mt-4">
                    <Label htmlFor="health_conditions">Health Conditions / Allergies</Label>
                    {isEditing ? (
                  <Textarea
                    id="health_conditions"
                        value={formData.health_conditions}
                    onChange={(e) => handleInputChange('health_conditions', e.target.value)}
                        className="mt-1"
                        rows={3}
                        placeholder="Any health conditions, allergies, or medical information we should know about..."
                  />
                    ) : (
                      <p className="mt-1 text-sm text-gray-900">{user.health_conditions || 'None specified'}</p>
                    )}
                </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
